import { IconCollapse, IconExpand } from '@ui/src';
import ExpandableRow from '../../../../common/aml/parts/expandable-row';
import { RiskScoringTypes } from '../../../../common/aml/types';
import { ApplicationType } from '../../../../../hoc/new-application/types';
import ExpandableRowContent from '../../../../common/aml/parts/expandable-row-content';
import DateOfIncorporation from '../../../../common/aml/parts/date-of-incorporation';
import { RiskScoreResultTypes } from '../types';

import cn from '../styles.module.css';

const RiskScoreResults = ({
  onExpandClick,
  expandedRows,
  setExpandedRows,
  aml,
  riskScoreRanges,
  type,
  countries,
  isEditable,
  clientsActivities,
  deliveryChannelsCorporate,
  deliveryChannelsIndividual,
  products,
  sicCodesDictionaries,
  sourcesOfFunds,
  monthlyTurnover,
  monthlyTurnoverCorporate,
}: RiskScoreResultTypes) => (
  <div className={cn.scoreResultWrapper}>
    <div className={cn.collapseButtonWrapper}>
      <button
        type="button"
        className={cn.collapseAllButton}
        onClick={onExpandClick}
      >
        {!expandedRows?.length ? 'Expand all' : 'Collapse all'}
        {!expandedRows?.length ? (
          <IconExpand width={15} height={15} className={cn.iconExpand} />
        ) : (
          <IconCollapse width={15} className={cn.iconCollapse} />
        )}
      </button>
    </div>
    <ExpandableRow
      name={RiskScoringTypes.GEOGRAPHICAL_RISK}
      title="Geographical risk"
      expandedRows={expandedRows}
      setExpandedRows={setExpandedRows}
      totalRisk={aml?.geographicalRiskTotal as number}
      riskScoreRanges={riskScoreRanges}
    >
      {type === ApplicationType.INDIVIDUAL && (
        <>
          <ExpandableRowContent
            title="Residency:"
            value={aml?.countryId as string}
            risk={
              aml?.countryRisk?.toString() !== 'null' &&
              aml?.countryRisk?.toString() !== 'undefined'
                ? (aml?.countryRisk?.toString() as string)
                : '-'
            }
            list={countries}
            name="residency"
            isEditable={isEditable}
          />
          <ExpandableRowContent
            title="Citizenship:"
            value={aml?.nationalityId as string}
            risk={
              aml?.nationalityRisk?.toString() !== 'null' &&
              aml?.nationalityRisk?.toString() !== 'undefined'
                ? (aml?.nationalityRisk?.toString() as string)
                : '-'
            }
            list={countries}
            name="citizenship"
            isEditable={isEditable}
          />
        </>
      )}
      {type === ApplicationType.CORPORATE && (
        <>
          <ExpandableRowContent
            title="Country of incorporation:"
            value={aml?.countryOfIncorporationId as string}
            risk={
              aml?.countryOfIncorporationRisk?.toString() !== 'null' &&
              aml?.countryOfIncorporationRisk?.toString() !== 'undefined'
                ? (aml?.countryOfIncorporationRisk?.toString() as string)
                : '-'
            }
            list={countries}
            name="countryOfIncorporation"
            isEditable={isEditable}
          />
          <ExpandableRowContent
            title="Citizenship of company officials"
            value=""
            risk={
              aml?.officialRiskTotal?.toString() !== 'null' &&
              aml?.officialRiskTotal?.toString() !== 'undefined'
                ? (aml?.officialRiskTotal?.toString() as string)
                : '-'
            }
            list={countries}
            name="citizenshipOfCompanyOfficials"
            officialRiskItems={aml?.officialRiskItems}
            isEditable={isEditable}
            withoutOpacity
          />
        </>
      )}
    </ExpandableRow>
    {type === ApplicationType.INDIVIDUAL && (
      <ExpandableRow
        name={RiskScoringTypes.TYPE_OF_CLIENT}
        title="Type of client"
        expandedRows={expandedRows}
        setExpandedRows={setExpandedRows}
        riskScoreRanges={riskScoreRanges}
        totalRisk={aml?.clientActivityRisk as number}
      >
        <ExpandableRowContent
          title="Activity of the client:"
          value={aml?.clientActivityId as string}
          risk={(aml?.clientActivityRisk as number) || '-'}
          list={clientsActivities}
          name="privateIndividual"
          isEditable={isEditable}
        />
      </ExpandableRow>
    )}
    <ExpandableRow
      name={RiskScoringTypes.DELIVERY_CHANNEL}
      title="Delivery channel"
      expandedRows={expandedRows}
      setExpandedRows={setExpandedRows}
      riskScoreRanges={riskScoreRanges}
      totalRisk={aml?.deliveryChannelRisk as number}
    >
      <ExpandableRowContent
        value={aml?.deliveryChannelId as string}
        risk={(aml?.deliveryChannelRisk as number) || '-'}
        list={
          type === ApplicationType.CORPORATE
            ? deliveryChannelsCorporate
            : deliveryChannelsIndividual
        }
        name="channel"
        isEditable={isEditable}
      />
    </ExpandableRow>
    <ExpandableRow
      name={RiskScoringTypes.PRODUCTS}
      title="Products"
      expandedRows={expandedRows}
      setExpandedRows={setExpandedRows}
      riskScoreRanges={riskScoreRanges}
      totalRisk={aml?.productsRiskTotal as number}
    >
      {aml?.productsRiskItems?.map((item) => (
        <ExpandableRowContent
          key={item.id}
          value={
            products?.find((product) => product.value === item.id)
              ?.name as string
          }
          risk={item.value || '-'}
          name={products?.find((product) => product.value === item.id)?.name}
          isEditable={isEditable}
        />
      ))}
    </ExpandableRow>
    {type === ApplicationType.CORPORATE && (
      <>
        <DateOfIncorporation
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          values={aml}
          riskScoreRanges={riskScoreRanges}
          isEditable={isEditable}
        />
        <ExpandableRow
          name={RiskScoringTypes.SIC_CODES}
          title="Sic Codes"
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          totalRisk={aml?.sicRiskTotal as number}
          riskScoreRanges={riskScoreRanges}
        >
          {aml?.sicRiskItems?.map((item) => (
            <ExpandableRowContent
              key={item.name}
              value={`${
                sicCodesDictionaries?.find(
                  (sicCode) => sicCode.name === item.name,
                )?.name as string
              } - ${
                sicCodesDictionaries?.find(
                  (sicCode) => sicCode.name === item.name,
                )?.description as string
              }
          `}
              risk={item.value || '-'}
              name={
                sicCodesDictionaries?.find(
                  (sicCode) => sicCode.name === item.name,
                )?.name
              }
              isEditable={isEditable}
            />
          ))}
        </ExpandableRow>
      </>
    )}
    {type === ApplicationType.INDIVIDUAL && (
      <ExpandableRow
        name={RiskScoringTypes.SOURCE_OF_FUNDS}
        title="Source of Funds"
        expandedRows={expandedRows}
        setExpandedRows={setExpandedRows}
        totalRisk={aml?.sourcesOfFundsRiskTotal as number}
        riskScoreRanges={riskScoreRanges}
      >
        {aml?.sourcesOfFundsRiskItems?.map((item) => (
          <ExpandableRowContent
            key={item.id}
            value={
              sourcesOfFunds?.find((source) => source.value === item.id)
                ?.name as string
            }
            risk={item.value || '-'}
            name={
              sourcesOfFunds?.find((source) => source.value === item.id)?.name
            }
            isEditable={isEditable}
          />
        ))}
      </ExpandableRow>
    )}
    <ExpandableRow
      name={RiskScoringTypes.MONTHLY_VOLUMES}
      title="Turnover"
      expandedRows={expandedRows}
      setExpandedRows={setExpandedRows}
      totalRisk={aml?.monthlyTurnoverRangeRisk as number}
      riskScoreRanges={riskScoreRanges}
    >
      <ExpandableRowContent
        title="Turnover:"
        value={aml?.monthlyTurnoverRangeId as string}
        risk={
          aml?.monthlyTurnoverRangeRisk?.toString() !== 'null' &&
          aml?.monthlyTurnoverRangeRisk?.toString() !== 'undefined'
            ? (aml?.monthlyTurnoverRangeRisk?.toString() as string)
            : '-'
        }
        list={
          type === ApplicationType.INDIVIDUAL
            ? monthlyTurnover
            : monthlyTurnoverCorporate
        }
        name="monthlyTurnoverRangeId"
        isEditable={isEditable}
      />
    </ExpandableRow>
  </div>
);

export default RiskScoreResults;
